/**
 * AI Garage Main Page Component
 * Full-featured hub with sidebar navigation matching reference design
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, Star, MessageSquare, LayoutDashboard, ShieldCheck, Settings as SettingsIcon,
  Home, User, ChevronLeft, ChevronRight,
  Moon, Sun, GitCompare, KeyRound, MessageSquarePlus, LogOut, ShieldAlert
} from 'lucide-react';
import GarageDiscoverView from './views/GarageDiscoverView';
import GarageCollectionView from './views/GarageCollectionView';
import GarageCompareView from './views/GarageCompareView';
import GarageRequestsView from './views/GarageRequestsView';
import GarageRequestDetailView from './views/GarageRequestDetailView';
import GarageDashboardView from './views/GarageDashboardView';
import GarageAdminView from './views/GarageAdminView';
import GarageSettingsView from './views/GarageSettingsView';
import GarageMakersView from './views/GarageMakersView';
import GarageRequestFlowView from './views/GarageRequestFlowView';
import GarageAccessRequestsView from './views/GarageAccessRequestsView';
import GarageSecurityScanView from './views/GarageSecurityScanView';
import NotificationBell from '../NotificationBell/NotificationBell';
import OnboardingModal from './modals/OnboardingModal';
import GarageFeedbackModal from './modals/GarageFeedbackModal';
import authService from '../../services/authService';
import { normalizeUserEmail } from '../../utils/userEmail';
import garageAPI from '../../services/garage/api';
import { launchExternalAgent } from '../../services/api';
import config from '../../config/environment';
import {
  formatAccessRole,
  hasAdminAccess,
  hasDashboardAccess,
} from './constants/roleAccess';
import './AIGarage.css';

const AIGaragePage = ({ userId, userEmail, user, onViewChange }) => {
  const [currentView, setCurrentView] = useState('discover');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [accessPersona, setAccessPersona] = useState(null);
  const [accessPersonas, setAccessPersonas] = useState([]);
  const [canAccessDashboard, setCanAccessDashboard] = useState(false);
  const [canViewAdminActivity, setCanViewAdminActivity] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [pinnedTools, setPinnedTools] = useState([]);
  const [recentTools, setRecentTools] = useState([]);
  const [roleChangeNotification, setRoleChangeNotification] = useState(null);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const userMenuRef = useRef(null);

  // Config version - increment this to trigger refresh in child components
  const [configVersion, setConfigVersion] = useState(0);

  // Cross-view navigation state
  const [initialMakerEmail, setInitialMakerEmail] = useState(null);
  const [initialToolId, setInitialToolId] = useState(undefined);
  const [requestDetailId, setRequestDetailId] = useState(null);
  const [compareToolIds, setCompareToolIds] = useState([]);
  const [initialAdminTab, setInitialAdminTab] = useState(null);
  const [initialRoiToolId, setInitialRoiToolId] = useState(null);
  // Which Requests tab to restore when leaving request detail (my-requests | admin-queue)
  const [requestsListTab, setRequestsListTab] = useState('my-requests');
  const [secScanRunId, setSecScanRunId] = useState(null);

  // Detail view state for showing back button in header
  const [detailViewState, setDetailViewState] = useState({
    isDetailView: false,
    backLabel: '',
    onBack: null
  });

  const authUser = authService.getUserInfo();
  const email = normalizeUserEmail(userEmail || user?.email || authUser?.email || userId) || 'unknown';

  // Get user name - priority: 1) Profile full name, 2) Profile display name, 3) User object, 4) Email prefix
  const getFullName = () => {
    console.log('🔍 Getting full name. userProfile:', userProfile);
    if (userProfile?.firstName && userProfile?.lastName) {
      console.log('✅ Using profile firstName + lastName:', `${userProfile.firstName} ${userProfile.lastName}`);
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    if (userProfile?.displayName) {
      console.log('✅ Using profile displayName:', userProfile.displayName);
      return userProfile.displayName;
    }
    const fallback = user?.name || user?.displayName || email.split('@')[0] || 'User';
    console.log('⚠️ Using fallback name:', fallback);
    return fallback;
  };

  const userName = getFullName();
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Get auth token for launching tools
  const authToken = authService.getToken() || '';

  // Check if user is admin and get role
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        console.log(`🔍 Checking admin status for: ${email}`);

        // Check via API
        const adminStatus = await garageAPI.checkAdminStatus(email);
        console.log('📊 Admin status response:', adminStatus);

        const role = adminStatus?.role || null;
        const isAdminFlag = adminStatus?.isAdmin || false;
        const dashboardAccess = Boolean(adminStatus?.canAccessDashboard);
        const prevRole = userRole;

        console.log(`👤 User role: ${role}, isAdmin: ${isAdminFlag}, canAccessDashboard: ${dashboardAccess}`);

        setUserRole(role);
        setAccessPersona(adminStatus?.accessPersona || null);
        setAccessPersonas(
          adminStatus?.accessPersonas
          || (adminStatus?.accessPersona ? [adminStatus.accessPersona] : [])
        );
        setCanAccessDashboard(dashboardAccess);
        setCanViewAdminActivity(Boolean(adminStatus?.canViewAdminActivity) && role === 'admin');

        // Admin if role is 'admin' or 'sales_admin' (reviewer/metrics_admin do NOT get admin UI access)
        const shouldBeAdmin = Boolean(isAdminFlag)
          && hasAdminAccess(
            role,
            adminStatus?.accessPersonas || adminStatus?.accessPersona
          );
        setIsAdmin(shouldBeAdmin);

        console.log(`✅ Setting isAdmin to: ${shouldBeAdmin} (role: ${role})`);

        // Show notification if role changed
        if (prevRole && prevRole !== role) {
          console.log(`🔄 User role changed: ${prevRole} → ${role}`);
          setRoleChangeNotification({
            from: formatAccessRole(prevRole),
            to: formatAccessRole(role),
          });
          // Auto-dismiss notification after 8 seconds
          setTimeout(() => setRoleChangeNotification(null), 8000);
        }
      } catch (error) {
        console.error('❌ Failed to check admin status:', error);
        // Fallback to JWT check
        const userInfo = authService.getUserInfo();
        const role = userInfo?.role || null;
        console.log('⚠️ Using fallback JWT role:', role);
        setUserRole(role);
        setIsAdmin(hasAdminAccess(role, null));
        setCanAccessDashboard(hasDashboardAccess(role, null));
        setCanViewAdminActivity(false);
      }
    };

    if (email && email !== 'unknown') {
      // Initial check
      checkAdmin();

      // Poll for role changes every 30 seconds
      // This ensures role changes are detected without requiring page refresh
      const pollInterval = setInterval(() => {
        checkAdmin();
      }, 30000); // 30 seconds

      // Cleanup interval on unmount or email change
      return () => clearInterval(pollInterval);
    }
  }, [email]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setUserMenuOpen(false);
    try {
      await authService.signOut();
    } catch (err) {
      console.error('Sign out failed', err);
      setSigningOut(false);
    }
  };

  // Check onboarding status on first load
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const profile = await garageAPI.getUserProfile(email);
        setUserProfile(profile);

        // Show onboarding if user hasn't completed it
        if (!profile || profile.onboardingStatus !== 'completed') {
          setShowOnboarding(true);
        }
        setOnboardingChecked(true);
      } catch (error) {
        // No profile exists - create one silently with 'user' role and show onboarding
        console.log('No user profile found, creating default user profile and showing onboarding');
        try {
          const newProfileData = {
            userEmail: email,
            displayName: user?.name || user?.displayName || email.split('@')[0] || 'User',
            role: 'user', // Default role
            onboardingStatus: 'pending', // Will be completed after onboarding
            preferences: {
              auto_created: true,
              created_at: new Date().toISOString()
            }
          };

          // Create the profile silently
          const createdProfile = await garageAPI.createUserProfile(newProfileData);
          setUserProfile(createdProfile);
          console.log('✅ Auto-created user profile for new user');
        } catch (createError) {
          console.error('Failed to auto-create user profile:', createError);
        }

        // Show onboarding wizard for user to set preferences
        setShowOnboarding(true);
        setOnboardingChecked(true);
      }
    };

    if (email && email !== 'unknown' && !onboardingChecked) {
      checkOnboardingStatus();
    }
  }, [email, onboardingChecked, user]);

  // Record login once per browser session after profile exists
  useEffect(() => {
    if (!email || email === 'unknown' || !userProfile) return;
    const key = `garageLoginRecorded:${email}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    garageAPI.recordUserLogin(email).catch((err) => {
      console.warn('Failed to record login metrics:', err?.message || err);
      sessionStorage.removeItem(key);
    });
  }, [email, userProfile]);

  // Load pinned tools
  useEffect(() => {
    const loadPinnedTools = async () => {
      try {
        const pins = await garageAPI.getUserPins(email);
        setPinnedTools(pins);
      } catch (error) {
        console.error('Failed to load pinned tools:', error);
      }
    };
    if (email && email !== 'unknown') {
      loadPinnedTools();
    }
  }, [email]);

  // Load recent tools
  useEffect(() => {
    const loadRecentTools = async () => {
      try {
        const recent = await garageAPI.getRecentTools(email, 5);
        setRecentTools(recent);
      } catch (error) {
        console.error('Failed to load recent tools:', error);
      }
    };
    if (email && email !== 'unknown') {
      loadRecentTools();
    }
  }, [email]);

  // Function to refresh pinned tools (called after bookmark add/remove)
  const refreshPinnedTools = async () => {
    try {
      const pins = await garageAPI.getUserPins(email);
      setPinnedTools(pins);
    } catch (error) {
      console.error('Failed to refresh pinned tools:', error);
    }
  };

  // Load theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('garage-theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.setAttribute('data-garage-theme', 'dark');
    }

    // Cleanup: Remove theme attribute when component unmounts to prevent leaking to main Ringo UI
    return () => {
      document.documentElement.removeAttribute('data-garage-theme');
    };
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('garage-theme', newTheme ? 'dark' : 'light');
    document.documentElement.setAttribute('data-garage-theme', newTheme ? 'dark' : 'light');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Update URL based on current view and state
  const updateUrl = (view, options = {}) => {
    let url = '/ai-garage';

    switch(view) {
      case 'discover':
        url = options.toolId ? `/ai-garage/discover/${options.toolId}` : '/ai-garage/discover';
        break;
      case 'collection':
        url = '/ai-garage/collection';
        break;
      case 'requests':
        url = ['admin-queue', 'dashboard'].includes(options.tab)
          ? `/ai-garage/requests?tab=${options.tab}`
          : '/ai-garage/requests';
        break;
      case 'request-detail':
        url = options.requestId ? `/ai-garage/request/${options.requestId}` : '/ai-garage/requests';
        break;
      case 'dashboard':
        url = '/ai-garage/dashboard';
        break;
      case 'admin':
        url = '/ai-garage/admin';
        break;
      case 'settings':
        url = '/ai-garage/settings';
        break;
      case 'makers':
        url = options.makerEmail ? `/ai-garage/makers/${options.makerEmail}` : '/ai-garage/makers';
        break;
      case 'request-flow':
        url = '/ai-garage/request-flow';
        break;
      case 'compare':
        url = '/ai-garage/compare';
        break;
      case 'access-requests':
        url = '/ai-garage/access-requests';
        break;
      case 'sec-scan':
        url = options.runId ? `/ai-garage/sec-scan/${options.runId}` : '/ai-garage/sec-scan';
        break;
      default:
        url = '/ai-garage';
    }

    window.history.pushState({ view, ...options }, '', url);
  };

  const getRequestsBackLabel = (tab) => (
    tab === 'dashboard' ? 'Back to Dashboard' : tab === 'admin-queue' ? 'Back to Admin queue' : 'Back to Requests'
  );

  const resolveRequestsTab = (tab) => (
    ['admin-queue', 'dashboard'].includes(tab) ? tab : 'my-requests'
  );

  // Parse URL and restore state
  const parseUrlAndRestoreState = () => {
    const path = window.location.pathname;
    const historyState = window.history.state || {};
    const searchParams = new URLSearchParams(window.location.search);

    // Match /ai-garage/request/:id
    const requestMatch = path.match(/\/ai-garage\/request\/([^/]+)/);
    if (requestMatch) {
      const requestId = requestMatch[1];
      const fromTab = resolveRequestsTab(
        historyState.tab || historyState.fromTab || searchParams.get('from') || requestsListTab
      );
      setRequestsListTab(fromTab);
      setRequestDetailId(requestId);
      setCurrentView('request-detail');
      setDetailViewState({
        isDetailView: true,
        backLabel: getRequestsBackLabel(fromTab),
        onBack: () => navigateToRequests({ tab: fromTab })
      });
      return;
    }

    // Match /ai-garage/requests (optional ?tab=admin-queue)
    if (path === '/ai-garage/requests' || path.endsWith('/ai-garage/requests')) {
      const tab = resolveRequestsTab(
        historyState.tab || searchParams.get('tab') || requestsListTab
      );
      setRequestsListTab(tab);
      setCurrentView('requests');
      return;
    }

    // Match /ai-garage/discover/:toolId
    const discoverMatch = path.match(/\/ai-garage\/discover\/([^/]+)/);
    if (discoverMatch) {
      const toolId = discoverMatch[1];
      setInitialToolId(toolId);
      setCurrentView('discover');
      return;
    }

    // Match /ai-garage/makers/:email
    const makersMatch = path.match(/\/ai-garage\/makers\/([^/]+)/);
    if (makersMatch) {
      const makerEmail = decodeURIComponent(makersMatch[1]);
      setInitialMakerEmail(makerEmail);
      setCurrentView('makers');
      return;
    }

    // Simple path matches
    if (path.endsWith('/discover')) {
      setCurrentView('discover');
    } else if (path.endsWith('/collection')) {
      setCurrentView('collection');
    } else if (path.endsWith('/dashboard')) {
      setCurrentView('dashboard');
    } else if (path.endsWith('/admin')) {
      setInitialAdminTab(searchParams.get('tab') || null);
      setInitialRoiToolId(searchParams.get('toolId') || null);
      setCurrentView('admin');
    } else if (path.endsWith('/settings')) {
      setCurrentView('settings');
    } else if (path.endsWith('/makers')) {
      setCurrentView('makers');
    } else if (path.endsWith('/request-flow')) {
      setCurrentView('request-flow');
    } else if (path.endsWith('/compare')) {
      setCurrentView('compare');
    } else if (path.endsWith('/access-requests')) {
      if (config.ACCESS_REQUESTS_ENABLED) {
        setCurrentView('access-requests');
      } else {
        setCurrentView('discover');
        window.history.replaceState({ view: 'discover' }, '', '/ai-garage/discover');
      }
    } else {
      const secScanMatch = path.match(/\/ai-garage\/sec-scan(?:\/([^/]+))?$/);
      if (secScanMatch && config.SEC_SCAN_ENABLED) {
        setSecScanRunId(secScanMatch[1] || null);
        setCurrentView('sec-scan');
        return;
      }
      if (path.includes('/ai-garage')) {
        setCurrentView('discover');
      } else {
        setCurrentView('discover');
        window.history.replaceState({ view: 'discover' }, '', '/ai-garage/discover');
      }
    }
  };

  // Initialize from URL on mount
  useEffect(() => {
    parseUrlAndRestoreState();
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      parseUrlAndRestoreState();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Admin reviews feedback — don't allow submit from Admin
  useEffect(() => {
    if (currentView === 'admin' && showFeedbackModal) {
      setShowFeedbackModal(false);
    }
  }, [currentView, showFeedbackModal]);

  const navigate = (view, options = {}) => {
    setCurrentView(view);
    // Clear navigation state when manually navigating
    setInitialMakerEmail(null);
    setInitialToolId(undefined); // Use undefined to force prop change detection
    setRequestDetailId(null);
    setInitialAdminTab(options.adminTab || null);
    setInitialRoiToolId(options.roiToolId || null);
    if (view !== 'sec-scan') {
      setSecScanRunId(null);
    } else if (options.runId) {
      setSecScanRunId(options.runId);
    }

    // Handle compare mode with initial tool IDs
    if (options.initialToolIds) {
      setCompareToolIds(options.initialToolIds);
    } else {
      setCompareToolIds([]);
    }

    if (view === 'requests' && options.tab) {
      setRequestsListTab(resolveRequestsTab(options.tab));
    }

    // Clear detail view state
    setDetailViewState({ isDetailView: false, backLabel: '', onBack: null });
    // Preserve current requests tab when opening via sidebar
    const urlOptions = view === 'requests'
      ? { ...options, tab: options.tab || requestsListTab }
      : options;
    updateUrl(view, urlOptions);
  };

  // Navigate to requests list (optionally restore Admin queue / My requests tab)
  const navigateToRequests = (options = {}) => {
    const tab = resolveRequestsTab(options.tab || requestsListTab);
    setRequestsListTab(tab);
    setCurrentView('requests');
    setRequestDetailId(null);
    setDetailViewState({
      isDetailView: false,
      backLabel: '',
      onBack: null
    });
    updateUrl('requests', { tab });
  };

  // Cross-view navigation: Navigate to a specific maker profile
  const navigateToMakerProfile = (makerEmail) => {
    setInitialMakerEmail(makerEmail);
    setInitialToolId(null);
    setCurrentView('makers');
    updateUrl('makers', { makerEmail });
  };

  // Cross-view navigation: Navigate to a specific tool detail
  const navigateToToolDetail = (toolId) => {
    setInitialToolId(toolId);
    setInitialMakerEmail(null);
    setCurrentView('discover');
    updateUrl('discover', { toolId });
  };

  // Navigate to request detail view
  // options.tab: which Requests tab to return to (my-requests | admin-queue)
  const navigateToRequestDetail = (requestId, options = {}) => {
    const returnTab = resolveRequestsTab(options.tab || requestsListTab);
    setRequestsListTab(returnTab);
    setRequestDetailId(requestId);
    setCurrentView('request-detail');
    const backLabel = getRequestsBackLabel(returnTab);
    setDetailViewState({
      isDetailView: true,
      backLabel,
      onBack: () => {
        navigateToRequests({ tab: returnTab });
      }
    });
    updateUrl('request-detail', { requestId, tab: returnTab });
  };

  // Handle profile updates from settings or onboarding
  const handleProfileUpdate = async (profileData) => {
    console.log('🔄 handleProfileUpdate called with:', profileData);
    try {
      // Reload the full profile from the backend to ensure consistency
      console.log('📡 Fetching updated profile for:', email);
      const updatedProfile = await garageAPI.getUserProfile(email);
      console.log('✅ Profile refreshed from backend:', updatedProfile);
      setUserProfile(updatedProfile);
    } catch (error) {
      console.error('❌ Failed to refresh profile:', error);
      // Fallback to local update if API fails
      console.log('⚠️ Using fallback local update');
      setUserProfile({ ...userProfile, ...profileData });
    }
  };

  // Handle onboarding save
  const handleOnboardingSave = async (preferences) => {
    try {
      const profileData = {
        userEmail: email,
        firstName: preferences.firstName || null,
        lastName: preferences.lastName || null,
        displayName: preferences.firstName && preferences.lastName
          ? `${preferences.firstName} ${preferences.lastName}`
          : userName,
        // IMPORTANT: DO NOT set role here to preserve existing admin/maker roles
        // Only set role: 'user' for NEW profiles, not updates
        onboardingStatus: 'completed',
        persona: preferences.persona,
        userRole: preferences.userRole,
        preferences: {
          hub_organization: preferences.hubOrganization,
          onboarding_completed: true,
          completed_at: new Date().toISOString()
        }
      };

      // Check if profile exists
      if (userProfile) {
        // Update existing profile - DON'T send role to preserve admin/maker status
        await garageAPI.updateUserProfile(email, profileData);
        console.log('✅ Updated user profile with onboarding preferences (preserved existing role)');
      } else {
        // Create new profile - only new users get 'user' role
        const newProfileData = { ...profileData, role: 'user' };
        await garageAPI.createUserProfile(newProfileData);
        console.log('✅ Created user profile with onboarding preferences');
      }

      // Refresh profile from backend
      await handleProfileUpdate(profileData);
      setShowOnboarding(false);
    } catch (error) {
      console.error('Failed to save onboarding preferences:', error);
      // Still close the modal even if save fails
      setShowOnboarding(false);
    }
  };

  // Handle onboarding skip
  const handleOnboardingSkip = async () => {
    try {
      const profileData = {
        userEmail: email,
        displayName: userName,
        // IMPORTANT: DO NOT set role here to preserve existing admin/maker roles
        onboardingStatus: 'completed', // Mark as completed but skipped
        persona: null,
        userRole: null,
        preferences: {
          onboarding_skipped: true,
          skipped_at: new Date().toISOString()
        }
      };

      // Check if profile exists
      if (userProfile) {
        // Update existing - preserve existing role
        await garageAPI.updateUserProfile(email, profileData);
        console.log('⏭️ User skipped onboarding (profile updated, preserved existing role)');
      } else {
        // Create new profile - only new users get 'user' role
        const newProfileData = { ...profileData, role: 'user' };
        await garageAPI.createUserProfile(newProfileData);
        console.log('⏭️ User skipped onboarding (profile created)');
      }

      setUserProfile({ ...userProfile, ...profileData });
      setShowOnboarding(false);
    } catch (error) {
      console.error('Failed to save skip status:', error);
      // Still close the modal even if save fails
      setShowOnboarding(false);
    }
  };

  const handleToolLaunch = async (tool) => {
    const isDynamicBotLaunch = Boolean(
      tool?.botLaunchOptions?.dynamic
      && tool?.botLaunchTarget
    );

    if (isDynamicBotLaunch) {
      // Open synchronously during the click event so the browser does not block
      // the destination while the backend resolves the conversation.
      const launchWindow = window.open('about:blank', '_blank');
      if (launchWindow) launchWindow.opener = null;
      const cacheKey = `ai-garage:bot-conversation:${tool.id}`;
      let conversationIdHint = null;
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached?.userEmail === email && Number(cached?.expiresAt || 0) > Date.now()) {
          conversationIdHint = cached.conversationId || null;
        }
      } catch (_) {
        sessionStorage.removeItem(cacheKey);
      }

      try {
        const resolved = await garageAPI.resolveBotLaunch(
          tool.id,
          tool.botLaunchTarget,
          conversationIdHint
        );
        sessionStorage.setItem(cacheKey, JSON.stringify({
          userEmail: email,
          conversationId: resolved.conversationId,
          credentialVersion: resolved.credentialVersion,
          expiresAt: Date.now() + (12 * 60 * 60 * 1000),
        }));

        try {
          localStorage.setItem('ai_garage_pending_roi_report', JSON.stringify({
            toolId: tool.id,
            name: tool.title,
            adminEstimate: tool.minPerSession ?? tool.min_per_session ?? null,
            sessionId: `launch-${tool.id}-${Date.now()}`,
            at: Date.now(),
          }));
        } catch (_) {}

        // The launch API logs usage after successfully resolving the URL.
        garageAPI.getRecentTools(email, 5).then(setRecentTools).catch(() => {});
        if (launchWindow) {
          launchWindow.location.replace(resolved.launchUrl);
        } else {
          window.location.href = resolved.launchUrl;
        }
      } catch (error) {
        if (launchWindow) launchWindow.close();
        console.error('Failed to resolve bot conversation:', error);
        const detail = error?.response?.data?.detail;
        const message = typeof detail === 'string'
          ? detail
          : (detail?.message || 'We could not open your bot conversation. Please try again or contact the bot owner.');
        window.alert(message);
      }
      return;
    }

    try {
      // Log the tool usage
      console.log(`🚀 Logging tool launch: ${tool.title} (${tool.id}) for user: ${email}`);
      await garageAPI.logToolUsage(tool.id, email);
      console.log('✅ Tool usage logged successfully');

      // Queue post-session ROI report when user returns to Garage
      try {
        localStorage.setItem(
          'ai_garage_pending_roi_report',
          JSON.stringify({
            toolId: tool.id,
            name: tool.title,
            adminEstimate: tool.minPerSession ?? tool.min_per_session ?? null,
            sessionId: `launch-${tool.id}-${Date.now()}`,
            at: Date.now(),
          })
        );
      } catch (_) {
        /* ignore storage errors */
      }

      // Reload recent tools
      console.log('🔄 Reloading recent tools...');
      const recent = await garageAPI.getRecentTools(email, 5);
      console.log(`✅ Recent tools loaded: ${recent.length} tools`, recent);
      setRecentTools(recent);

      // Use the same launch pattern as AI Hub - convert tool to agent format
      // This ensures consistent behavior for integrated tools, external URLs, and SSO
      const agentFormat = {
        name: tool.title,
        agent_name: tool.title,
        env_url: tool.url
      };

      // Launch using the unified launchExternalAgent function
      // This handles integrated routes (/legal-translator, /account-plan, etc.)
      // and external URLs correctly
      console.log('🚀 Launching tool using launchExternalAgent:', agentFormat);
      launchExternalAgent(agentFormat, authService);

    } catch (error) {
      console.error('❌ Failed to launch tool:', error);
      console.error('Error details:', {
        message: error.message,
        toolId: tool?.id,
        userEmail: email
      });
      // Fallback: Still try to launch the tool even if logging fails
      try {
        const agentFormat = {
          name: tool.title,
          agent_name: tool.title,
          env_url: tool.url
        };
        launchExternalAgent(agentFormat, authService);
      } catch (launchError) {
        console.error('❌ Fallback launch also failed:', launchError);
        // Last resort: direct window.open
        window.open(tool.url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const renderView = () => {
    // Handler for when configuration changes (roles, teams, etc.)
    const handleConfigChange = () => {
      setConfigVersion(prev => prev + 1);
    };

    const viewProps = {
      userEmail: email,
      userName: userName,
      isAdmin,
      userRole, // NEW: Pass user role to child views
      accessPersona,
      accessPersonas,
      canAccessDashboard,
      canViewAdminActivity,
      onActivityAccessChange: setCanViewAdminActivity,
      onNavigate: navigate,
      onToolLaunch: handleToolLaunch,
      recentTools,
      setRecentTools,
      // Cross-view navigation
      onNavigateToMaker: navigateToMakerProfile,
      onNavigateToTool: navigateToToolDetail,
      onOpenTool: navigateToToolDetail, // Alias for onNavigateToTool
      onNavigateToRequestDetail: navigateToRequestDetail,
      // Detail view state control
      setDetailViewState,
      // Profile update callback
      onProfileUpdate: handleProfileUpdate,
      userProfile: userProfile,
      // Sidebar refresh callback
      onRefreshPinnedTools: refreshPinnedTools,
      // Config change callback and version
      onConfigChange: handleConfigChange,
      configVersion: configVersion,
      // Gate help tours until onboarding is checked and finished/skipped
      showOnboarding,
      onboardingChecked
    };

    switch (currentView) {
      case 'discover':
        return <GarageDiscoverView {...viewProps} initialToolId={initialToolId} />;
      case 'collection':
        return <GarageCollectionView {...viewProps} />;
      case 'compare':
        return <GarageCompareView {...viewProps} initialToolIds={compareToolIds} />;
      case 'requests':
        return <GarageRequestsView {...viewProps} initialTab={requestsListTab} />;
      case 'access-requests':
        return config.ACCESS_REQUESTS_ENABLED ? (
          <GarageAccessRequestsView {...viewProps} />
        ) : (
          <GarageDiscoverView {...viewProps} initialToolId={initialToolId} />
        );
      case 'sec-scan':
        return config.SEC_SCAN_ENABLED ? (
          <GarageSecurityScanView
            {...viewProps}
            initialRunId={secScanRunId}
            onOpenRun={(runId) => {
              setSecScanRunId(runId);
              updateUrl('sec-scan', { runId });
            }}
            onClearRun={() => {
              setSecScanRunId(null);
              updateUrl('sec-scan');
            }}
          />
        ) : (
          <GarageDiscoverView {...viewProps} initialToolId={initialToolId} />
        );
      case 'request-detail':
        return (
          <GarageRequestDetailView
            {...viewProps}
            requestId={requestDetailId}
            onBack={detailViewState.onBack}
            backLabel={detailViewState.backLabel || 'Back to My Requests'}
          />
        );
      case 'dashboard':
        // ROI Time Saved is available to all authenticated users
        return <GarageDashboardView {...viewProps} />;
      case 'admin':
        return isAdmin ? (
          <GarageAdminView
            {...viewProps}
            initialTab={initialAdminTab}
            initialRoiToolId={initialRoiToolId}
          />
        ) : (
          <GarageDiscoverView {...viewProps} />
        );
      case 'settings':
        return <GarageSettingsView {...viewProps} userInitials={userInitials} />;
      case 'makers':
        // Only show makers view if feature flag is enabled
        return process.env.REACT_APP_SHOW_MAKER_FEATURES === 'true'
          ? <GarageMakersView {...viewProps} initialMakerEmail={initialMakerEmail} />
          : <GarageDiscoverView {...viewProps} initialToolId={initialToolId} />;
      case 'request-flow':
        return <GarageRequestFlowView {...viewProps} />;
      default:
        return <GarageDiscoverView {...viewProps} initialToolId={initialToolId} />;
    }
  };

  return (
    <div
      className={`garage-app ${sidebarCollapsed && !sidebarHovered ? 'sidebar-collapsed' : ''} ${isAdmin ? 'is-admin' : ''}`}
      data-garage-theme={isDarkMode ? 'dark' : 'light'}
    >
      {/* Sidebar */}
      <aside
        className="garage-sidebar"
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="garage-brand">
          <div className="garage-brand-mark">
            <img src="/ai-garage-icon.png" alt="" aria-hidden="true" />
          </div>
          <div className="garage-brand-text">
            <p className="garage-brand-name">AI Garage</p>
            <p className="garage-brand-sub">AI Apps & Tools</p>
          </div>
          <button
            className="garage-sidebar-toggle"
            onClick={toggleSidebar}
            title="Collapse sidebar"
          >
            {sidebarCollapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
          </button>
        </div>

        {/* Main Navigation */}
        <button
          className={`garage-nav-item ${currentView === 'discover' ? 'active' : ''}`}
          onClick={() => navigate('discover')}
          title={sidebarCollapsed ? 'Discover' : ''}
        >
          <Grid size={16} />
          <span className="garage-nav-label">Discover</span>
        </button>

        <button
          className={`garage-nav-item ${currentView === 'collection' ? 'active' : ''}`}
          onClick={() => navigate('collection')}
          title={sidebarCollapsed ? 'My collection' : ''}
        >
          <Star size={16} />
          <span className="garage-nav-label">My collection</span>
        </button>

        <button
          className={`garage-nav-item ${currentView === 'compare' ? 'active' : ''}`}
          onClick={() => navigate('compare')}
          title={sidebarCollapsed ? 'Compare tools' : ''}
        >
          <GitCompare size={16} />
          <span className="garage-nav-label">Compare tools</span>
        </button>

        <button
          className={`garage-nav-item ${currentView === 'requests' ? 'active' : ''}`}
          onClick={() => navigate('requests')}
          title={sidebarCollapsed ? 'My requests' : ''}
        >
          <MessageSquare size={16} />
          <span className="garage-nav-label">My requests</span>
        </button>

        {config.ACCESS_REQUESTS_ENABLED && (
          <button
            className={`garage-nav-item ${currentView === 'access-requests' ? 'active' : ''}`}
            onClick={() => navigate('access-requests')}
            title={sidebarCollapsed ? 'Access requests' : ''}
          >
            <KeyRound size={16} />
            <span className="garage-nav-label">Access requests</span>
          </button>
        )}

        {config.SEC_SCAN_ENABLED && (
          <button
            className={`garage-nav-item ${currentView === 'sec-scan' ? 'active' : ''}`}
            onClick={() => navigate('sec-scan')}
            title={sidebarCollapsed ? 'Scans' : ''}
          >
            <ShieldAlert size={16} />
            <span className="garage-nav-label">Scans</span>
          </button>
        )}

        {/* Makers - Hidden via feature flag */}
        {process.env.REACT_APP_SHOW_MAKER_FEATURES === 'true' && (
          <button
            className={`garage-nav-item ${currentView === 'makers' ? 'active' : ''}`}
            onClick={() => navigate('makers')}
            title={sidebarCollapsed ? 'Makers' : ''}
          >
            <User size={16} />
            <span className="garage-nav-label">Makers</span>
          </button>
        )}

        {/* Tools Dashboard — all users (personal Time Saved / admin ROI+Usage) */}
        <button
          className={`garage-nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
          onClick={() => navigate('dashboard')}
          title={sidebarCollapsed ? 'Tools dashboard' : ''}
        >
          <LayoutDashboard size={16} />
          <span className="garage-nav-label">Tools dashboard</span>
        </button>

        {/* Admin Only Navigation */}
        {isAdmin && (
          <button
            className={`garage-nav-item ${currentView === 'admin' ? 'active' : ''}`}
            onClick={() => navigate('admin')}
            title={sidebarCollapsed ? 'Admin' : ''}
            data-admin-only
          >
            <ShieldCheck size={16} />
            <span className="garage-nav-label">Admin</span>
          </button>
        )}

        {/* Pinned Section */}
        <div className="garage-nav-section" data-tour="discover-pinned">Pinned</div>
        {pinnedTools.length === 0 ? (
          <div className="garage-empty-pins">
            <span className="garage-nav-label">No pinned tools yet</span>
          </div>
        ) : (
          pinnedTools.map((pin) => (
            <button
              key={pin.id}
              className="garage-recent-item"
              onClick={() => navigateToToolDetail(pin.tool?.id || pin.toolId)}
              title={pin.tool?.title}
            >
              {pin.tool?.title || 'Unknown Tool'}
            </button>
          ))
        )}

        {/* Recent Section */}
        <div className="garage-nav-section">Recent</div>
        {recentTools.length === 0 ? (
          <div className="garage-empty-pins">
            <span className="garage-nav-label">No recent tools yet</span>
          </div>
        ) : (
          recentTools.map((tool) => (
            <button
              key={tool.id}
              className="garage-recent-item"
              onClick={() => navigateToToolDetail(tool.id)}
              title={tool.title}
            >
              {tool.title}
            </button>
          ))
        )}

        {/* Spacer to push settings and user to bottom */}
        <div style={{ flex: 1 }}></div>

        {/* Settings - Moved to bottom */}
        <button
          className={`garage-nav-item ${currentView === 'settings' ? 'active' : ''}`}
          onClick={() => navigate('settings')}
          title={sidebarCollapsed ? 'Settings' : ''}
        >
          <SettingsIcon size={16} />
          <span className="garage-nav-label">Settings</span>
        </button>

        {/* User Block + Sign out */}
        <div className="garage-user-menu" ref={userMenuRef}>
          <button
            type="button"
            className={`garage-user-block ${userMenuOpen ? 'open' : ''}`}
            title={userEmail}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            onClick={() => setUserMenuOpen((v) => !v)}
          >
            <div className="garage-avatar">{userInitials}</div>
            <div className="garage-user-block-text">
              <p className="garage-user-name">{userName}</p>
              <div className="garage-user-role-row">
                <span className={`garage-user-role-badge garage-status-${userRole || userProfile?.role || 'user'}`}>
                  {userRole || userProfile?.role || (isAdmin ? 'admin' : 'user')}
                </span>
              </div>
            </div>
          </button>
          {userMenuOpen && (
            <div className="garage-user-dropdown" role="menu">
              <div className="garage-user-dropdown-meta">
                <span className="garage-user-dropdown-email">{userEmail || email}</span>
              </div>
              <button
                type="button"
                className="garage-user-dropdown-item"
                role="menuitem"
                disabled={signingOut}
                onClick={handleSignOut}
              >
                <LogOut size={14} />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="garage-main">
        {/* Topbar */}
        <div className="garage-topbar">
          <div className="garage-crumb">
            {detailViewState.isDetailView && detailViewState.onBack ? (
              <>
                <button
                  className="garage-header-back-btn"
                  onClick={detailViewState.onBack}
                  title={detailViewState.backLabel}
                >
                  <ChevronLeft size={16} />
                  {detailViewState.backLabel}
                </button>
                <ChevronRight size={14} className="garage-crumb-separator" />
              </>
            ) : null}

            <span
              className="garage-crumb-link"
              onClick={() => navigate('discover')}
              style={{ cursor: 'pointer' }}
            >
              AI Garage
            </span>

            {currentView !== 'discover' && (
              <>
                <ChevronRight size={14} className="garage-crumb-separator" />
                {currentView === 'collection' && <span>My collection</span>}
                {currentView === 'compare' && <span>Compare tools</span>}
                {currentView === 'requests' && <span>My requests</span>}
                {currentView === 'access-requests' && <span>Access requests</span>}
                {currentView === 'request-detail' && <span>Request Details</span>}
                {currentView === 'dashboard' && <span>Tools dashboard</span>}
                {currentView === 'admin' && <span>Admin</span>}
                {currentView === 'settings' && <span>Settings</span>}
                {currentView === 'makers' && process.env.REACT_APP_SHOW_MAKER_FEATURES === 'true' && <span>List of Makers</span>}
                {currentView === 'request-flow' && <span>Submit request</span>}
              </>
            )}
          </div>
          <div className="garage-topbar-actions">
            <button
              type="button"
              className={`garage-theme-toggle garage-feedback-toggle${currentView === 'admin' ? ' is-disabled' : ''}`}
              onClick={() => {
                if (currentView === 'admin') return;
                setShowFeedbackModal(true);
              }}
              disabled={currentView === 'admin'}
              aria-disabled={currentView === 'admin'}
              title={
                currentView === 'admin'
                  ? 'Submit feedback from Discover or other pages — Admin is for reviewing feedback'
                  : 'Submit feedback'
              }
            >
              <MessageSquarePlus size={16} />
            </button>
            <NotificationBell userEmail={email} onNotificationNavigate={navigateToRequestDetail} />
            <button
              className="garage-theme-toggle"
              onClick={toggleTheme}
              title="Toggle theme"
            >
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        {/* View Container */}
        <div className="garage-view-container">
          {renderView()}
        </div>
      </div>

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onSave={handleOnboardingSave}
        onSkip={handleOnboardingSkip}
        userEmail={email}
        userName={userName}
        configVersion={configVersion}
      />

      <GarageFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        userEmail={email}
        userName={userName}
        onSubmit={async (data) => {
          await garageAPI.submitFeedback(data);
        }}
      />

      {/* Role Change Notification */}
      {roleChangeNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideInRight 0.3s ease-out',
          maxWidth: '400px'
        }}>
          <ShieldCheck size={24} />
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              Role Updated
            </div>
            <div style={{ fontSize: '14px', opacity: 0.95 }}>
              Your role changed from {roleChangeNotification.from} to {roleChangeNotification.to}
            </div>
          </div>
          <button
            onClick={() => setRoleChangeNotification(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              marginLeft: 'auto',
              opacity: 0.8,
              fontSize: '20px',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default AIGaragePage;
